import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { invoicesApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, MoreVertical, FileText, Eye, XCircle, CheckCircle, Download, Loader2, AlertTriangle, FileCode, Archive, Send } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [cancelDialog, setCancelDialog] = useState({ open: false, invoice: null });
  const [cancelForm, setCancelForm] = useState({ reason: "", reason_description: "" });
  const [cancelling, setCancelling] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      const response = await invoicesApi.list();
      setInvoices(response.data);
    } catch (error) {
      toast.error("Error al cargar facturas");
    } finally {
      setLoading(false);
    }
  };

  const handleIssue = async (invoiceId) => {
    try {
      await invoicesApi.issue(invoiceId);
      toast.success("Factura timbrada exitosamente");
      loadInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al timbrar factura");
    }
  };

  const handleCancelClick = (invoice) => {
    setCancelDialog({ open: true, invoice });
    setCancelForm({ reason: "", reason_description: "" });
  };

  const handleCancelSubmit = async () => {
    if (!cancelForm.reason) {
      toast.error("Selecciona un motivo de cancelación");
      return;
    }
    
    setCancelling(true);
    try {
      const token = localStorage.getItem("cia_token");
      await axios.post(
        `${API}/invoices/${cancelDialog.invoice.id}/cancel`,
        cancelForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Solicitud de cancelación enviada. El cliente debe aceptarla.");
      setCancelDialog({ open: false, invoice: null });
      loadInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cancelar factura");
    } finally {
      setCancelling(false);
    }
  };

  const handleViewPdf = (invoiceId) => {
    window.open(`${API}/invoices/${invoiceId}/pdf`, "_blank");
  };

  const handleViewXml = (invoiceId) => {
    window.open(`${API}/invoices/${invoiceId}/xml`, "_blank");
  };

  const handleDownloadZip = async (invoice) => {
    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.get(
        `${API}/invoices/${invoice.id}/download-zip`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `factura_${invoice.series}-${invoice.folio}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Archivos descargados");
    } catch (error) {
      toast.error("Error al descargar archivos");
    }
  };

  const handleResend = async (invoiceId) => {
    try {
      const token = localStorage.getItem("cia_token");
      await axios.post(
        `${API}/invoices/${invoiceId}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Factura reenviada al cliente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al reenviar factura");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('es-MX');
    } catch {
      return '-';
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: "badge-draft",
      issued: "badge-issued",
      cancelled: "badge-cancelled",
      cancellation_pending: "bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium"
    };
    const labels = {
      draft: "Borrador",
      issued: "Timbrada",
      cancelled: "Cancelada",
      cancellation_pending: "Cancelación pendiente"
    };
    return <span className={badges[status] || badges.draft}>{labels[status] || status}</span>;
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = 
      invoice.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.folio.includes(searchTerm) ||
      invoice.client_rfc.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || invoice.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9BD5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="page-title">Facturas</h1>
          <p className="page-subtitle">Administra tus facturas electrónicas</p>
        </div>
        <Link to="/dashboard/invoices/new">
          <Button className="cia-btn-primary w-full sm:w-auto" data-testid="new-invoice-btn">
            <Plus className="w-5 h-5 mr-2" />
            Nueva Factura
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="cia-card">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 cia-input"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="draft">Borrador</SelectItem>
                <SelectItem value="issued">Timbrada</SelectItem>
                <SelectItem value="cancellation_pending">Cancelación pendiente</SelectItem>
                <SelectItem value="cancelled">Cancelada</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Invoices table */}
      <Card className="cia-card">
        <CardContent className="p-0">
          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No hay facturas</h3>
              <p className="text-slate-500 mb-6">
                {searchTerm || statusFilter !== "all" 
                  ? "No se encontraron facturas con los filtros aplicados"
                  : "Crea tu primera factura para comenzar"}
              </p>
              {!searchTerm && statusFilter === "all" && (
                <Link to="/dashboard/invoices/new">
                  <Button className="cia-btn-primary">Crear Factura</Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto -mx-3 sm:mx-0">
              <table className="data-table min-w-[600px] sm:min-w-full">
                <thead>
                  <tr>
                    <th>Folio</th>
                    <th>Cliente</th>
                    <th className="hidden sm:table-cell">RFC</th>
                    <th>Total</th>
                    <th className="hidden md:table-cell">Saldo</th>
                    <th>Estado</th>
                    <th className="hidden sm:table-cell">Fecha</th>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                      <td className="font-medium text-xs sm:text-sm">{invoice.series}-{invoice.folio}</td>
                      <td className="max-w-[120px] truncate">{invoice.client_name}</td>
                      <td className="font-mono text-xs hidden sm:table-cell">{invoice.client_rfc}</td>
                      <td className="font-medium text-xs sm:text-sm">{formatCurrency(invoice.total)}</td>
                      <td className="hidden md:table-cell">
                        {invoice.status === "issued" ? (
                          <span className={`font-medium text-xs sm:text-sm ${invoice.balance_due > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {formatCurrency(invoice.balance_due || invoice.total)}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td>{getStatusBadge(invoice.status)}</td>
                      <td className="text-slate-500 hidden sm:table-cell">
                        {formatDate(invoice.created_at)}
                      </td>
                      <td className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`invoice-actions-${invoice.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => navigate(`/dashboard/invoices/${invoice.id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Ver Detalles
                            </DropdownMenuItem>
                            
                            {invoice.status === "draft" && (
                              <DropdownMenuItem onClick={() => handleIssue(invoice.id)}>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Timbrar
                              </DropdownMenuItem>
                            )}
                            
                            {invoice.status === "issued" && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleViewPdf(invoice.id)}>
                                  <FileText className="w-4 h-4 mr-2" />
                                  Ver PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleViewXml(invoice.id)}>
                                  <FileCode className="w-4 h-4 mr-2" />
                                  Ver XML
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDownloadZip(invoice)}>
                                  <Archive className="w-4 h-4 mr-2" />
                                  Descargar ZIP
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleResend(invoice.id)}>
                                  <Send className="w-4 h-4 mr-2" />
                                  Reenviar
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleCancelClick(invoice)} className="text-red-600">
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Cancelar
                                </DropdownMenuItem>
                              </>
                            )}
                            
                            {invoice.status === "cancellation_pending" && (
                              <DropdownMenuItem disabled>
                                <AlertTriangle className="w-4 h-4 mr-2 text-orange-500" />
                                Esperando aceptación
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cancel Invoice Dialog */}
      <Dialog open={cancelDialog.open} onOpenChange={(open) => setCancelDialog({ ...cancelDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar Factura</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5" />
                <div>
                  <p className="text-orange-800 font-medium text-sm">Proceso de cancelación</p>
                  <p className="text-orange-700 text-xs">
                    El cliente recibirá una notificación y deberá aceptar la cancelación ante el SAT.
                    El estado cambiará a "Cancelación pendiente" hasta que el cliente acepte.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Motivo de cancelación *</Label>
              <Select value={cancelForm.reason} onValueChange={(v) => setCancelForm({ ...cancelForm, reason: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona el motivo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="01">01 - Comprobante emitido con errores con relación</SelectItem>
                  <SelectItem value="02">02 - Comprobante emitido con errores sin relación</SelectItem>
                  <SelectItem value="03">03 - No se llevó a cabo la operación</SelectItem>
                  <SelectItem value="04">04 - Operación nominativa relacionada con factura global</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Descripción adicional (opcional)</Label>
              <Textarea
                value={cancelForm.reason_description}
                onChange={(e) => setCancelForm({ ...cancelForm, reason_description: e.target.value })}
                placeholder="Explica brevemente el motivo de la cancelación..."
                rows={3}
              />
            </div>

            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setCancelDialog({ open: false, invoice: null })}>
                Cancelar
              </Button>
              <Button 
                onClick={handleCancelSubmit} 
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={cancelling}
              >
                {cancelling ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Solicitar Cancelación"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesPage;
